import { useMemo, useState } from "react";
import { useStore } from "../data/store";
import { Room } from "../ui/Room";
import { Btn, Bar, Check, Empty, Sheet } from "../ui/bits";
import { useFocus } from "../ui/useFocus";
import { Health } from "../ui/Health";
import { formatJalali, toFa } from "@soheil/core";
import { timeOfDay } from "@soheil/core";
import { roomState } from "@soheil/core";
import { levelName } from "@soheil/core";
import { streakAtRisk } from "@soheil/core";
import { useAtLimit, useEntitlement } from "../data/entitlement";
import { useToast } from "../ui/Toast";

export function Today() {
  const { state, dispatch, today } = useStore();
  const activeTasks = state.tasks.filter((t) => !t.completedAt && !t.deletedAt).length;
  const ent = useEntitlement();
  const tasksAtLimit = useAtLimit("activeTasks", activeTasks);
  const limitToast = useToast();
  const focus = useFocus();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const room = useMemo(
    () => roomState(state.progress.longestStreak, state.progress.studyMinutes),
    [state.progress.longestStreak, state.progress.studyMinutes],
  );

  const topThree = state.tasks
    .filter((t) => !t.deletedAt && t.isTopThree)
    .slice(0, 3);
  const rest = state.tasks.filter((t) => !t.deletedAt && !t.isTopThree && !t.completedAt);

  const todayStars = state.stars.filter((s) => s.localDate === today).reduce((a, b) => a + b.delta, 0);
  const atRisk = streakAtRisk(state.progress, today, {
    vacationFrom: state.settings?.vacationFrom,
    vacationUntil: state.settings?.vacationUntil,
  });

  const askEnergy = state.settings.energyAskedOn !== today;

  const label = focus.phase === "running" ? "در حال مطالعه"
    : focus.phase === "paused" ? "متوقف شد"
    : focus.phase === "done" ? "جلسه تمام شد! ⭐"
    : focus.phase === "break" ? "وقت استراحت"
    : "جلسهٔ تمرکز";

  return (
    <div className="screen">
      <header className="daybar">
        <div>
          <h1>{state.settings.displayName ? `سلام ${state.settings.displayName}` : "امروز"}</h1>
          <span>{formatJalali(today, { withWeekday: true })}</span>
        </div>
        <div className="daybar-stats">
          <span className="pill" title="زنجیره">🔥 {toFa(state.progress.currentStreak)}</span>
          <span className="pill" title="ستارهٔ امروز">⭐ {toFa(todayStars)}</span>
        </div>
      </header>

      <Room pose={focus.pose} time={timeOfDay()} unlocked={room.unlocked} clock={focus.clock} />

      <div className="roomstrip">
        <span>{levelName(state.progress.level)} · اتاق سطح {toFa(room.level)}</span>
        {room.next && (
          <span className="roomnext">
            {room.next.emoji} {room.next.name} — {room.next.hint}
          </span>
        )}
      </div>

      <section className="timer">
        <div className="timer-row">
          <div>
            <div className="timer-num">{toFa(focus.clock)}</div>
            <div className="timer-lbl">{label}</div>
          </div>
          <div className="timer-actions">
            {focus.phase === "idle" && <Btn onClick={() => focus.start()}>شروع</Btn>}
            {focus.phase === "running" && <Btn variant="ghost" onClick={focus.pause}>توقف</Btn>}
            {focus.phase === "paused" && (
              <>
                <Btn onClick={focus.resume}>ادامه</Btn>
                <Btn variant="quiet" onClick={focus.abandon}>رها</Btn>
              </>
            )}
            {focus.phase === "done" && <Btn onClick={focus.startBreak}>استراحت</Btn>}
            {focus.phase === "break" && <Btn variant="ghost" onClick={focus.dismiss}>پایان استراحت</Btn>}
          </div>
        </div>
        <Bar pct={focus.phase === "break" ? 100 : focus.progress * 100} />
      </section>

      {atRisk && state.progress.currentStreak > 0 && (
        <div className="nudge">
          زنجیرهٔ {toFa(state.progress.currentStreak)} روزه‌ات امروز هنوز ثبتی ندارد.
          {state.progress.freezeTokens > 0 && ` اگر نشد، یک «شب ابری» داری.`}
        </div>
      )}

      {askEnergy && (
        <section className="energy">
          <b>امروز چقدر انرژی داری؟</b>
          <div className="energy-row">
            {([["low", "کم"], ["normal", "معمولی"], ["high", "زیاد"]] as const).map(([k, t]) => (
              <button key={k} onClick={() => dispatch({ type: "setSettings", patch: { energyToday: k, energyAskedOn: today } })}>
                {t}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="block">
        <div className="block-head">
          <h2>سه کار مهم امروز</h2>
          <button
            className="link"
            onClick={() => {
              if (tasksAtLimit) {
                limitToast(`نسخهٔ رایگان تا ${toFa(ent.limits.activeTasks)} کار باز است — یکی را تمام کن یا در «من» ارتقا بده`);
                return;
              }
              setAdding(true);
            }}
          >
            {tasksAtLimit ? "افزودن 🔒" : "افزودن"}
          </button>
        </div>
        {topThree.length === 0 ? (
          <Empty icon="⭐" title="هنوز کاری انتخاب نکرده‌ای" hint="سه کار مهم، از لیست بلند بهتر است." />
        ) : (
          <ul className="list">
            {topThree.map((t) => (
              <li key={t.id}>
                <button className="row" onClick={() => dispatch({ type: "toggleTask", id: t.id, day: today })}>
                  <Check done={!!t.completedAt} />
                  <span className={t.completedAt ? "done" : ""}>{t.title}</span>
                  {t.domain && <em>{t.domain}</em>}
                </button>
              </li>
            ))}
          </ul>
        )}
        {rest.length > 0 && (
          <details className="more">
            <summary>بقیهٔ کارها ({toFa(rest.length)})</summary>
            <ul className="list">
              {rest.map((t) => (
                <li key={t.id}>
                  <button className="row" onClick={() => dispatch({ type: "toggleTask", id: t.id, day: today })}>
                    <Check done={!!t.completedAt} />
                    <span>{t.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <Health />

      <Sheet open={adding} onClose={() => setAdding(false)} title="کار جدید">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const v = draft.trim();
            if (!v) return;
            dispatch({ type: "addTask", title: v, dueDate: today });
            setDraft("");
            setAdding(false);
          }}
        >
          <input
            className="input"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="مثلاً: دو ساعت زیست"
          />
          <Btn type="submit" full>افزودن</Btn>
        </form>
      </Sheet>
    </div>
  );
}
